import { defineMessages } from 'react-intl';

/**
 * Shared utilities for version history components (Rezepte, Produkte, Rohstoffe)
 */

/**
 * Common i18n messages used across all version history components
 */
export const versionHistoryMessages = defineMessages({
  versionHistory: {
    id: 'Version History',
    defaultMessage: 'Versionshistorie',
  },
  version: {
    id: 'Version',
    defaultMessage: 'Version',
  },
  operation: {
    id: 'Operation',
    defaultMessage: 'Aktion',
  },
  changedBy: {
    id: 'Changed By',
    defaultMessage: 'Geändert von',
  },
  changedAt: {
    id: 'Changed At',
    defaultMessage: 'Geändert am',
  },
  description: {
    id: 'Description',
    defaultMessage: 'Beschreibung',
  },
  actions: {
    id: 'Actions',
    defaultMessage: 'Aktionen',
  },
  restore: {
    id: 'Restore',
    defaultMessage: 'Wiederherstellen',
  },
  viewDetails: {
    id: 'View Details',
    defaultMessage: 'Details anzeigen',
  },
  close: {
    id: 'Close',
    defaultMessage: 'Schließen',
  },
  noHistory: {
    id: 'No history available',
    defaultMessage: 'Keine Versionshistorie verfügbar',
  },
  confirmRestore: {
    id: 'Confirm Restore',
    defaultMessage: 'Möchten Sie wirklich zu Version {version} zurückkehren?',
  },
  changes: {
    id: 'Changes',
    defaultMessage: 'Änderungen',
  },
  oldValue: {
    id: 'Old Value',
    defaultMessage: 'Alter Wert',
  },
  newValue: {
    id: 'New Value',
    defaultMessage: 'Neuer Wert',
  },
  restoreSuccessRezept: {
    id: 'Restore Success Rezept',
    defaultMessage: 'Rezept wurde erfolgreich wiederhergestellt',
  },
  restoreSuccessProdukt: {
    id: 'Restore Success Produkt',
    defaultMessage: 'Produkt wurde erfolgreich wiederhergestellt',
  },
  restoreSuccessRohstoff: {
    id: 'Restore Success Rohstoff',
    defaultMessage: 'Rohstoff wurde erfolgreich wiederhergestellt',
  },
  operationCreate: {
    id: 'Operation Create',
    defaultMessage: 'Erstellt',
  },
  operationUpdate: {
    id: 'Operation Update',
    defaultMessage: 'Aktualisiert',
  },
  operationDelete: {
    id: 'Operation Delete',
    defaultMessage: 'Gelöscht',
  },
  operationRestore: {
    id: 'Operation Restore',
    defaultMessage: 'Wiederhergestellt',
  },
});

/**
 * Operation type labels with colors for display
 * Use intl.formatMessage(opLabel.message) to get the translated text
 */
export const operationLabels = {
  CREATE: { message: versionHistoryMessages.operationCreate, color: 'green' },
  UPDATE: { message: versionHistoryMessages.operationUpdate, color: 'blue' },
  DELETE: { message: versionHistoryMessages.operationDelete, color: 'red' },
  RESTORE: {
    message: versionHistoryMessages.operationRestore,
    color: 'purple',
  },
};

/**
 * Format an ISO date string to German locale format
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string or '-' if input is falsy
 */
export const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Fields to ignore when displaying changes
 */
export const ignoredFields = ['modified'];

/**
 * Metadata fields to skip when expanding nested object changes (e.g. verkaufsinformation)
 */
const nestedObjectMetaFields = [
  'id',
  'rezepte_id',
  'user_id',
  'crdate',
  'modified',
  'deleted',
  'produktinfonummer',
];

/**
 * Mappings for enum-like fields to human-readable labels
 */
export const statusLabels = {
  1: 'aktuell',
  2: 'stillgelegt',
  3: 'in Bearbeitung',
};

export const internExternLabels = {
  true: 'intern',
  false: 'extern',
  0: 'extern',
  1: 'intern',
};

export const booleanLabels = {
  true: 'Ja',
  false: 'Nein',
  1: 'Ja',
  0: 'Nein',
  2: 'Ja', // In some cases, 2 is also used to represent true (e.g. in Produkt)
};

export const extern_internLabels = {
  false: 'extern',
  true: 'intern',
};

const booleanFields = ['jungprodukt', 'is_import', 'aktuell'];

const timestampFields = ['aktuell_expire'];

/**
 * Format an enum-like value to its human-readable label
 * @param {string} field - Field name
 * @param {*} value - Raw value
 * @returns {string|null} Formatted value or null if not an enum field
 */
export const formatEnumValue = (field, value) => {
  if (field === 'intern_extern') {
    return internExternLabels[String(value)] || String(value);
  }
  if (field === 'status' || field === 'rezeptstatus') {
    return statusLabels[value] || String(value);
  }
  if (booleanFields.includes(field)) {
    return (
      booleanLabels[value] ?? booleanLabels[String(value)] ?? String(value)
    );
  }
  if (timestampFields.includes(field)) {
    if (!value || value === 0) return '-';
    return formatDateTime(new Date(value * 1000).toISOString());
  }
  return null;
};

/**
 * Extract list of changed field names from a changes object
 * @param {Object} changes - Changes object with values_changed, values_added, values_removed
 * @returns {string[]} Array of changed field names
 */
export const getChangedFields = (changes) => {
  if (!changes) return [];
  const { values_changed, values_added, values_removed } = changes;
  const fields = new Set();
  if (values_changed)
    Object.keys(values_changed)
      .filter((f) => !ignoredFields.includes(f))
      .forEach((f) => fields.add(f));
  if (values_added)
    Object.keys(values_added)
      .filter((f) => !ignoredFields.includes(f))
      .forEach((f) => fields.add(f));
  if (values_removed)
    Object.keys(values_removed)
      .filter((f) => !ignoredFields.includes(f))
      .forEach((f) => fields.add(f));
  return Array.from(fields);
};

/**
 * Filter changes object to remove ignored fields
 * @param {Object} changes - Changes object with values_changed, values_added, values_removed
 * @returns {Object} Object with filtered changes and hasChanges boolean
 */
/**
 * Check if a value is a plain object (not array, not null)
 */
const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

/**
 * Filter a nested object's keys to only meaningful (non-meta) fields
 */
const filterNestedKeys = (obj) =>
  Object.keys(obj).filter((k) => !nestedObjectMetaFields.includes(k));

export const filterChanges = (changes) => {
  if (!changes) {
    return {
      filteredChanged: {},
      filteredAdded: {},
      filteredRemoved: {},
      hasChanges: false,
    };
  }

  const { values_changed, values_added, values_removed } = changes;

  // Build filteredChanged, expanding nested objects into sub-field entries
  // Also route null→object to filteredAdded and object→null to filteredRemoved
  const filteredChanged = {};
  const nestedAdded = {};
  const nestedRemoved = {};
  if (values_changed) {
    Object.entries(values_changed)
      .filter(([f]) => !ignoredFields.includes(f))
      .forEach(([field, value]) => {
        if (isPlainObject(value.old_value) && isPlainObject(value.new_value)) {
          // Both are objects: expand, only include sub-fields that actually differ
          filterNestedKeys(value.new_value).forEach((subField) => {
            const oldVal = value.old_value[subField];
            const newVal = value.new_value[subField];
            if (String(oldVal) !== String(newVal)) {
              filteredChanged[`${field}: ${subField}`] = {
                old_value: oldVal ?? '-',
                new_value: newVal ?? '-',
              };
            }
          });
        } else if (!value.old_value && isPlainObject(value.new_value)) {
          // null → object: treat as added
          filterNestedKeys(value.new_value).forEach((subField) => {
            const val = value.new_value[subField];
            if (val !== '' && val !== null && val !== undefined) {
              nestedAdded[`${field}: ${subField}`] = val;
            }
          });
        } else if (isPlainObject(value.old_value) && !value.new_value) {
          // object → null: treat as removed
          filterNestedKeys(value.old_value).forEach((subField) => {
            const val = value.old_value[subField];
            if (val !== '' && val !== null && val !== undefined) {
              nestedRemoved[`${field}: ${subField}`] = val;
            }
          });
        } else {
          filteredChanged[field] = value;
        }
      });
  }

  // Build filteredAdded, expanding nested objects into sub-field entries
  // Start with entries routed from values_changed (null → object)
  const filteredAdded = { ...nestedAdded };
  if (values_added) {
    Object.entries(values_added)
      .filter(([f]) => !ignoredFields.includes(f))
      .forEach(([field, value]) => {
        if (isPlainObject(value)) {
          filterNestedKeys(value).forEach((subField) => {
            const val = value[subField];
            if (val !== '' && val !== null && val !== undefined) {
              filteredAdded[`${field}: ${subField}`] = val;
            }
          });
        } else {
          filteredAdded[field] = value;
        }
      });
  }

  // Build filteredRemoved, expanding nested objects into sub-field entries
  // Start with entries routed from values_changed (object → null)
  const filteredRemoved = { ...nestedRemoved };
  if (values_removed) {
    Object.entries(values_removed)
      .filter(([f]) => !ignoredFields.includes(f))
      .forEach(([field, value]) => {
        if (isPlainObject(value)) {
          filterNestedKeys(value).forEach((subField) => {
            const val = value[subField];
            if (val !== '' && val !== null && val !== undefined) {
              filteredRemoved[`${field}: ${subField}`] = val;
            }
          });
        } else {
          filteredRemoved[field] = value;
        }
      });
  }

  const hasChanges =
    Object.keys(filteredChanged).length > 0 ||
    Object.keys(filteredAdded).length > 0 ||
    Object.keys(filteredRemoved).length > 0;

  return {
    filteredChanged,
    filteredAdded,
    filteredRemoved,
    hasChanges,
  };
};
